import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pagination, Pill, Text } from '$components'
import { EditIcon, BinIcon, PersonCircleIcon } from '$components'
import { ElementIntent } from '$components'
import { userDeleteMutation, userIndexOptions, userIndexQueryKey } from '$api-client/@tanstack/react-query.gen'
import { useModalState } from '$components'
import { ConfirmationModal } from '$components'
import { GenericTable } from '$components'
import { createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react'
import { Role, User } from '$api-client/types.gen'
import { TeamRoutePath } from '../routes/protected'
import { useNavigate } from 'react-router-dom'
import { Router } from '$utils/router'
import { AnimatedOutlet } from '$components'
import { useAuth } from '$features/auth/hooks/use-auth'
import { PermissionsHelper } from '$utils/permissions'
import { TextCell } from '$components'
import { OptionsCell } from '$components'
import { usePage } from '$hooks/use-page'
import { useTenant } from '$features/auth/hooks/use-tenant'
import { SUBSCRIPTION_EXPIRED_MESSAGE } from '$constants/subscription'
import { PersonCell } from '$components'
import { useTableSort } from '$hooks/use-table-sort'
import { ToastType } from '$components'
import { ToastHelper } from '$components'
import { usePageTitle } from '$components'
import { TableSkeleton } from '$components'

const columnHelper = createColumnHelper<User>()

export function MembersPage() {
  usePageTitle('Team Members')

  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { modalState, open, close } = useModalState()
  const { user } = useAuth()
  const { page, setPage } = usePage()
  const { hasSubscriptionExpired } = useTenant()
  const { getSorting } = useTableSort()

  const { data: users, isLoading } = useQuery({
    ...userIndexOptions({
      query: {
        include_deleted: true,
        page: page,
        sort: getSorting(),
      },
    }),
  })

  const { mutate: deleteUser, isPending: isDeleting } = useMutation({
    ...userDeleteMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userIndexQueryKey() })
      ToastHelper.deleteSuccess(ToastType.TeamMember)
      close()
    },
    onError: () => ToastHelper.deleteError(ToastType.TeamMember),
  })

  const columns = useMemo(() => [
    columnHelper.accessor('first_name', {
      id: 'name',
      header: 'Name',
      cell: ({ row }) => <PersonCell data={row.original} />,
    }),
    columnHelper.accessor('email', {
      header: 'Email',
      cell: info => (
        <TextCell>
          {info.getValue()}
        </TextCell>
      ),
    }),
    columnHelper.accessor('role', {
      header: 'Role',
      cell: info => (
        <Pill>
          <span className="capitalize">
            {info.getValue()}
          </span>
        </Pill>
      ),
    }),
    columnHelper.accessor('deleted_at', {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Pill variant={row.original.deleted_at ? 'error' : 'success'}>
          {row.original.deleted_at ? 'Deleted' : 'Active'}
        </Pill>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      maxSize: 66,
      minSize: 66,
      cell: info => (
        <OptionsCell
          options={[
            {
              id: 'edit',
              value: 'Edit',
              icon: EditIcon,
              onClick: () => navigate(Router.getRoute(TeamRoutePath.TeamUpdateMember, { id: info.row.original.id })),
              disabled: hasSubscriptionExpired || PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN]) || !!info.row.original.deleted_at,
              tooltip: hasSubscriptionExpired
                ? SUBSCRIPTION_EXPIRED_MESSAGE
                : PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN])
                  ? 'You are not allowed to edit users.'
                  : undefined,
            },
            {
              id: 'delete',
              value: 'Delete',
              intent: ElementIntent.Danger,
              icon: BinIcon,
              onClick: () => open(info.row.original.id),
              disabled: info.row.original.role === Role.SUPER_ADMIN
                || PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN])
                || !!info.row.original.deleted_at,
              tooltip: PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN])
                ? 'You are not allowed to delete users.'
                : info.row.original.role === Role.SUPER_ADMIN
                  ? 'Owner of the organisation cannot be deleted.'
                  : info.row.original.deleted_at !== null
                    ? 'This user has already been deleted.'
                    : undefined,
            },
          ]}
        />
      ),
    }),
  ], [open, navigate])

  if (isLoading) {
    return <TableSkeleton
      columns={5}
      rows={10}
    />
  }

  if (!users?.data.length) {
    return (
      <div className="w-full h-48 border rounded-card-sm flex flex-col items-center justify-center">
        <div className="size-12 flex items-center justify-center bg-border rounded-card-sm mb-stack-roomy">
          <PersonCircleIcon className="size-6 text-fg-subtle" />
        </div>

        <Text
          type="h4"
          className="font-medium mb-stack-dense"
        >
          No team members yet
        </Text>

        <Text
          type="s"
          className="text-fg-subtle"
        >
          Invite team members to your organisation.
        </Text>
      </div>
    )
  }

  const onConfirmDelete = () => {
    deleteUser({
      path: {
        id: modalState.modelId,
      },
    })
  }

  const getUserName = (id: number) => {
    const user = users.data.find(user => user.id === id)
    return user ? `${user.first_name} ${user.last_name}` : ''
  }

  return (
    <div>
      <GenericTable.Container>
        <GenericTable
          data={users.data}
          columns={columns}
          rowLink={(member) => Router.getRoute(TeamRoutePath.TeamMemberView, { id: member.id })}
        />

        <Pagination
          totalItems={users.meta.total}
          currentPage={page}
          totalPages={users.meta.last_page}
          onChange={setPage}
        />
      </GenericTable.Container>

      { modalState.isOpen && (
        <ConfirmationModal
          onClose={close}
          onConfirm={onConfirmDelete}
          title="Delete User"
          description={`Are you sure you want to remove ${getUserName(modalState.modelId)} from the system?`}
          isLoading={isDeleting}
          intent={ElementIntent.Danger}
        />
      )}

      <AnimatedOutlet />
    </div>
  )
}
